// apps/vendor/src/main.tsx
//
// Phase 1 Task 44 — vendor SPA entry. Same composition as admin/merchant.
// Phase 7 Task 01 — bindings refresh:
// vg-binding: P7.D-06   (real-time updates via WebSocket ticket flow)
// vg-binding: P7.D-146  (port 3003 + performance targets FCP <1.5s)
// vg-binding: P7.D-147  (Dark/Light mode toggle — ThemeProvider drives `dark`
//                        class on <html>; forceTheme dropped so toggle works)

import '@printway/ui/styles.css';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, Toaster, type Theme } from '@printway/ui';
import { apiClient } from '@printway/shared/api';

import { App } from './App.js';
import { I18nProvider } from './lib/i18n/index.js';
// Moved out of this file so non-component code can reach the same instance.
import { queryClient } from './lib/queryClient.js';
import { wireVendorSessionTeardown } from './lib/sessionTeardown.js';

// Clear the query cache on every session end (logout, failed silent refresh,
// cross-tab logout). Without this, signing in as a different vendor in the same
// tab rendered the previous account's cached data until a hard F5.
wireVendorSessionTeardown();

async function persistThemePreference(theme: Theme): Promise<void> {
  try {
    const client = apiClient();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const c = client as any;
    if (typeof c.PATCH === 'function') {
      await c.PATCH('/users/me/preferences', { body: { themePreference: theme } });
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[vendor] failed to persist theme preference', err);
  }
}

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Root element #root not found in vendor index.html');
}

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider
        defaultTheme="light"
        onThemeChange={persistThemePreference}
      >
        <I18nProvider>
          <Toaster>
            <App />
          </Toaster>
        </I18nProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </StrictMode>,
);
